import { StorageManager } from 'src/app/common/storage-manager.class';
import {
    Form,
    FormField,
    FormSection,
    FormType,
    InputType,
    FilteredValue,
    DropdownFilterContext,
    ReactiveDropdownCatalogs,
    ReactiveDropdownDataSource,
    ReactiveDropdownKeySelector,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { TrazabilidadEnvironmentDefinition } from '../../environment-variables';
import { TrazabilidadListsDefinition } from './list-variables';
import { TrazabilidadModel } from 'src/app/models/stock/trazabilidad.model';
import { SectorModel } from 'src/app/models/form-common/sector.model';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';

export interface TrazabilidadDropdownEndpoints {
    readonly proveedores: unknown;
    readonly fincas: unknown;
    readonly sectores: unknown;
    readonly parcelas: unknown;
    readonly cultivos: unknown;
    readonly variedades: unknown;
}

export class TrazabilidadFormDefinition {

    public formType!: FormType;
    public env: TrazabilidadEnvironmentDefinition = new TrazabilidadEnvironmentDefinition();
    public lists: TrazabilidadListsDefinition = new TrazabilidadListsDefinition();

    public cliente = StorageManager.getClient();
    public usuario = StorageManager.getUser();

    public model: ReactiveFormValue<TrazabilidadModel> = {} as ReactiveFormValue<TrazabilidadModel>;

    public tag: ReactiveFormChangedField<TrazabilidadModel> = '';
    public formFields!: Form<TrazabilidadModel>;
    public fieldNames: FormField<any>[] = [];
    public fieldLabels: string[] = [];

    private readonly dropdownEndpoints: TrazabilidadDropdownEndpoints | null;

    constructor(
        type: FormType,
        _model: Record<string, string>,
        table: (string[] | string[][])[] | null,
        dropdownEndpoints: TrazabilidadDropdownEndpoints | null = null
    ) {
        this.formType = +type;
        this.dropdownEndpoints = dropdownEndpoints;

        this.formFields = {
            name: 'trazabilidad',
            header: 'Trazabilidad',
            type: this.formType,
            sections: this.getFormSections(),
            table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
            uploadButton: { isEnabled: false, label: '' },
            signed: false,
            dropdownCatalogs: this.getDropdownCatalogs(),
            serializer: {
                fields: {
                    cultivo: { mode: 'optionRawValue', backendValueKey: 'cultivo' },
                    variedad: { mode: 'optionRawValue', backendValueKey: 'variedad' },
                    maceta_reciclada: { mode: 'boolean', trueValue: 1, falseValue: 0 }
                }
            }
        };
    }


    private getDropdownCatalogs(): ReactiveDropdownCatalogs {
        return {
            critical: ['id_finca', 'id_sector', 'id_parcela'],
            deferred: ['proveedor', 'cultivo', 'variedad'],
            lazy: []
        };
    }

    public getFormFields(): FormField<any>[] {
        const formFields: FormField<any>[] = [
            {
                name: 'fecha_recepcion',
                label: 'Fecha Recepción',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: this.formType < 3 ? 'proveedor' : 'proveedor_nombre',
                label: 'Proveedor',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.proveedores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<any>): boolean => it.value?.tipo === 'Proveedor'
                    }
                },
                ...this.createDropdownDataSourceField('proveedores', 'razon_social', 'id'),
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 2
            },
            {
                name: 'id_finca',
                label: this.env.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.fincas,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id'
                    }
                },
                ...this.createDropdownDataSourceField('fincas', 'nombre', 'id'),
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
            {
                name: this.formType < 3 ? 'id_sector' : 'sector_nombre',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < 3 ? 'id_sector' : 'sector_nombre',
                        kind: 'filter'
                    }
                ],
                label: this.env.sectorName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.sectores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<SectorModel>, context?: DropdownFilterContext<SectorModel>): boolean => {
                            if (it.value == null) {
                                return true;
                            }

                            const fincaId = this.getContextValue(context, 'id_finca');

                            if (fincaId === null) {
                                return false;
                            }

                            return this.sameValue(it.value.id_finca, fincaId);
                        }
                    }
                },
                ...this.createDropdownDataSourceField('sectores', 'nombre', 'id'),
                dependencyBehaviour: {
                    filterLocally: true,
                    clearOnParentChange: true,
                    clearOnHydration: false,
                    keepValueUntilOptionsLoaded: true,
                    preserveValueWhenOptionsMissing: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 4
            },
            {
                name: this.formType < 3 ? 'id_parcela' : 'parcela_nombre',
                dependencies: [
                    {
                        source: this.formType < 3 ? 'id_sector' : 'sector_nombre',
                        target: this.formType < 3 ? 'id_parcela' : 'parcela_nombre',
                        kind: 'filter'
                    }
                ],
                label: this.env.parcelaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.parcelas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<ParcelasModel>, context?: DropdownFilterContext<ParcelasModel>): boolean => {
                            if (it.value == null) {
                                return true;
                            }

                            const sectorField = this.formType < 3 ? 'id_sector' : 'sector_nombre';
                            const sectorValue = this.getContextValue(context, sectorField);

                            if (sectorValue === null) {
                                return false;
                            }

                            return this.sameValue(it.value.id_sector, sectorValue) || this.sameValue(it.value.sector, sectorValue);
                        }
                    }
                },
                ...this.createDropdownDataSourceField('parcelas', 'nombre', 'id'),
                dependencyBehaviour: {
                    filterLocally: true,
                    clearOnParentChange: true,
                    clearOnHydration: false,
                    keepValueUntilOptionsLoaded: true,
                    preserveValueWhenOptionsMissing: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 5
            },
            {
                name: 'cultivo',
                label: 'Cultivo',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.cultivos,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'cultivo'
                    }
                },
                ...this.createDropdownDataSourceField('cultivos', 'cultivo', 'cultivo'),
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 6
            },
            {
                name: 'variedad',
                dependencies: [
                    {
                        source: 'cultivo',
                        target: 'variedad',
                        kind: 'filter'
                    }
                ],
                label: 'Variedad',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.variedades,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'variedad',
                        filter: (
                            it: FilteredValue<VisitasVariedadMesModel>,
                            context?: DropdownFilterContext<VisitasVariedadMesModel>
                        ): boolean => {
                            if (it.value == null) {
                                return true;
                            }

                            const cultivo = this.getContextValue(context, 'cultivo');

                            if (cultivo === null) {
                                return false;
                            }

                            return this.sameValue(it.value.cultivo, cultivo);
                        }
                    }
                },
                ...this.createDropdownDataSourceField('variedades', 'variedad', ['cultivo', 'variedad']),
                dependencyBehaviour: {
                    filterLocally: true,
                    clearOnParentChange: true,
                    clearOnHydration: false,
                    keepValueUntilOptionsLoaded: true,
                    preserveValueWhenOptionsMissing: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 7
            },
            {
                name: 'semana_entrega_prevista',
                label: 'Semana Entrega Prevista',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 8
            },
            {
                name: 'incidencia',
                label: 'Incidencia',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.incidencias,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 9
            },
            {
                name: 'cantidad',
                label: 'Cantidad',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 10
            },
            {
                name: 'formato',
                label: 'Formato',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.formatos,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 11
            },
            {
                name: 'maceta_reciclada',
                label: 'Maceta Reciclada',
                input: { type: InputType.SWITCH, defaultValue: '0', isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 12
            },
            {
                name: 'cantidad_maceta_reciclada',
                label: 'Cantidad Maceta Reciclada',
                input: { type: InputType.NUMERIC, isEditable: true },
                visibleWhen: {
                    dependsOn: ['maceta_reciclada'],
                    resolve: (context): boolean => this.isSwitchEnabled(context.getValue('maceta_reciclada'))
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 13
            },
            {
                name: 'fecha_expedicion',
                label: 'Fecha Expedición',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 14
            },
        ];

        this.fieldNames = this.fieldNames.concat(formFields.filter(it => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(formFields.filter(it => it.conditions.isVisible).map(it => it.label));
        return formFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getHiddenFields(): FormField<any>[] {
        const hiddenFields: FormField<any>[] = [
            {
                name: 'id',
                label: '',
                input: { type: InputType.HIDDEN, defaultValue: this.model.id, isEditable: false },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: false,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: 'id_cliente',
                label: '',
                input: { type: InputType.HIDDEN, defaultValue: this.cliente?.id ?? '', isEditable: false },
                updateValue: () => this.cliente?.id ?? '',
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.hasClients
                },
                order: 2
            },
            {
                name: 'id_usuario',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.formType !== FormType.EDITION ? this.usuario?.id ?? '' : '',
                    isEditable: false
                },
                updateValue: () => this.formType !== FormType.EDITION ? this.usuario?.id ?? '' : '',
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
        ];

        return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }


    private createDropdownDataSourceField(
        endpointKey: keyof TrazabilidadDropdownEndpoints,
        labelKey: string,
        distinctBy: string | readonly string[]
    ): Partial<Pick<FormField<any>, 'dataSource'>> {
        const dataSource = this.createDropdownDataSource(endpointKey, labelKey, distinctBy);

        return dataSource === undefined ? {} : { dataSource };
    }

    private createDropdownDataSource(
        endpointKey: keyof TrazabilidadDropdownEndpoints,
        labelKey: string,
        distinctBy: string | readonly string[]
    ): ReactiveDropdownDataSource<TrazabilidadModel> | undefined {
        const endpoint = this.dropdownEndpoints?.[endpointKey];

        if (endpoint === null || endpoint === undefined) {
            return undefined;
        }

        const normalizedDistinctBy: ReactiveDropdownKeySelector<TrazabilidadModel> = typeof distinctBy === 'string' ? distinctBy : [...distinctBy];

        return {
            endpoint,
            cacheKey: `stock-trazabilidad-${String(endpointKey)}`,
            labelKey,
            distinctBy: normalizedDistinctBy,
            cacheEmptyResults: false
        };
    }

    private isSwitchEnabled(value: unknown): boolean {
        if (value === true || value === 1) {
            return true;
        }

        if (typeof value !== 'string') {
            return false;
        }

        const normalizedValue = value.trim().toLowerCase();

        return normalizedValue === '1' || normalizedValue === 'true' || normalizedValue === 'sí' || normalizedValue === 'si';
    }

    private getContextValue<T>(context: DropdownFilterContext<T> | undefined, fieldName: string): unknown | null {
        const controlValue = context?.model.get(fieldName)?.value;

        if (this.hasValue(controlValue)) {
            return controlValue;
        }

        const modelValue = this.model[fieldName as keyof TrazabilidadModel];

        return this.hasValue(modelValue) ? modelValue : null;
    }

    private hasValue(value: unknown): boolean {
        return value !== null && value !== undefined && String(value).length > 0;
    }

    private sameValue(left: unknown, right: unknown): boolean {
        if (!this.hasValue(left) || !this.hasValue(right)) {
            return false;
        }

        return String(left) === String(right);
    }

    private getFormSections(): FormSection<any>[] {
        const sections: FormSection<any>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getFormFields(),
                order: 1
            },
            {
                name: '',
                width: 6,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 2
            }
        ];
        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
}